import React from 'react';

import { formatDate } from '@betterment/core';
import { track, useAutoNavigate } from '@betterment/js-runtime';
import {
  ErrorText,
  Flex,
  FormikDropdown,
  FormikMultiSelect,
  FormikTextInput,
  Label,
  MultiSelectItem,
} from '@betterment/style-closet-react';
import { subYears } from 'date-fns';
import { ErrorMessage, Form, Formik, FormikHelpers } from 'formik';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { CategoryIconMap } from './TransactionIcon';

import FormikAutoSubmitter from '../../Shared/components/FormikAutoSubmitter';
import { DateFilter, LegacyDateFilter } from '../../Shared/date-filter';

interface FilterFormValues {
  to?: string;
  from?: string;
  dateFilter: DateFilter;
  legacyDateFilter: LegacyDateFilter;
  categories: string[];
}

interface DateFilterOptionItem {
  value: DateFilter | LegacyDateFilter;
  id: string;
}

interface Props {
  initialValues: FilterFormValues;
  disabled?: boolean;
}

const FilterContainer: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <Flex.Item width="100%" maxWidth={['none', '24rem']} mr="6" mb="6">
    {children}
  </Flex.Item>
);

const categoryMultiSelectItems: MultiSelectItem[] = Object.keys(CategoryIconMap).map(category => ({
  value: category,
  text: category,
}));

const dateFilterOptions: DateFilterOptionItem[] = Object.entries(DateFilter).map(([id, value]) => ({ id, value }));

const today = new Date();
const formattedToday = formatDate(today, 'iso8601');

const filterSchema = Yup.object().shape({
  dateFilter: Yup.string().required(),
  to: Yup.date()
    .max(formattedToday, 'Date cannot be in the future.')
    .when('from', { is: (val?: Date) => !!val, then: schema => schema.min(Yup.ref('from'), 'Date range is invalid') }),
  from: Yup.date()
    .max(formattedToday, 'Date cannot be in the future.')
    .min(formatDate(subYears(today, 2), 'iso8601'), 'Date must be within the last 2 years'),
  legacyDateFilter: Yup.string().required(),
});

export const TransactionFilters: React.FC<React.PropsWithChildren<Props>> = ({ initialValues, disabled }) => {
  const navigate = useAutoNavigate();
  const location = useLocation();

  const submitSearch = (values: FilterFormValues, { setFieldError }: FormikHelpers<FilterFormValues>) => {
    const query = new URLSearchParams(location.search);
    if (values.dateFilter === DateFilter.Custom) {
      if (values.to) {
        query.set('to', values.to);
      } else if (initialValues.to) {
        setFieldError('to', 'Date cannot be blank');
        return;
      }

      if (values.from) {
        query.set('from', values.from);
      } else if (initialValues.from) {
        setFieldError('from', 'Date cannot be blank');
        return;
      }
    } else {
      query.delete('from');
      query.delete('to');
    }

    query.set('dateFilter', values.dateFilter);
    if (values.categories !== initialValues.categories) {
      query.delete('categories[]');
      values.categories.forEach(cat => query.append('categories[]', cat));
    }

    void navigate(`?${query.toString()}`);
  };

  return (
    <Formik<FilterFormValues> initialValues={initialValues} onSubmit={submitSearch} validationSchema={filterSchema}>
      {({ values }) => (
        <Form>
          <FormikAutoSubmitter debounce={800} />
          <Flex
            flexDirection={['column', 'row']}
            flexWrap="wrap"
            pt="8"
            pb="6"
            borderBottom="1px solid"
            borderColor="bmtGrey20"
          >
            <>
              <FilterContainer>
                <FormikDropdown
                  labelText="Date range"
                  items={dateFilterOptions}
                  visibleItems={6}
                  name="dateFilter"
                  onChange={val => {
                    track('ElementClicked', { name: (val as DateFilterOptionItem).id, module: 'FilterTransactions' });
                  }}
                  disabled={disabled}
                />
              </FilterContainer>
              {[initialValues.dateFilter, values.dateFilter].includes(DateFilter.Custom) && (
                <>
                  <FilterContainer>
                    <Label mb="3" htmlFor="from-date" id="from-date-label">
                      From
                    </Label>
                    <FormikTextInput
                      mb="0"
                      lineHeight="100%"
                      borderRadius="3px"
                      name="from"
                      id="from-date"
                      type="date"
                      min={formatDate(subYears(today, 2), 'iso8601')}
                      max={formattedToday}
                      disabled={disabled}
                    />
                    <ErrorMessage name="from" component={ErrorText} aria-live="polite" />
                  </FilterContainer>
                  <FilterContainer>
                    <Label mb="3" htmlFor="to-date" id="to-date-label">
                      To
                    </Label>
                    <FormikTextInput
                      mb="0"
                      lineHeight="100%"
                      borderRadius="3px"
                      name="to"
                      id="to-date"
                      type="date"
                      min={initialValues.from}
                      max={formattedToday}
                      disabled={disabled}
                    />
                    <ErrorMessage name="to" component={ErrorText} aria-live="polite" />
                  </FilterContainer>
                </>
              )}
              <FilterContainer>
                <FormikMultiSelect
                  labelText="Categories"
                  name="categories"
                  items={categoryMultiSelectItems}
                  id="checking-categories-multiselect"
                  placeholder="All categories"
                  disabled={disabled}
                  invalid={false}
                  onChange={() => track('ElementClicked', { name: 'SelectCategories', module: 'FilterTransaction' })}
                />
              </FilterContainer>
            </>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
